import { useNavigate } from 'react-router-dom';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FileSignature, Plus, ChevronRight, Link2, Search, X, AlertTriangle } from 'lucide-react';
import api from '../services/api';
import { useAuthStore } from '../store/useStore';
import IntestazioneSezione from '../components/comuni/IntestazioneSezione';
import ErroreLettura from '../components/comuni/ErroreLettura';
import { conUnTentativo, motivoDellaLettura } from '../utils/primaLettura';
import EditorTemplate from '../components/pazienti/EditorTemplate';
import useAzione from '../navigazione/useAzione';
import { notificaConferma, notificaErrore, messaggioDa } from '../utils/notifiche';
import '../styles/ModelliConsenso.css';

/**
 * ModelliConsenso — l'elenco dei modelli di consenso.
 *
 * PERCHE' ESISTE
 * Prima del 17 settembre 2026 i modelli si gestivano SOLO da dentro la
 * scheda di una prestazione: per sapere cosa c'era bisognava aprire una
 * prestazione per volta, e per sapere a cosa era legato un consenso
 * bisognava aprirlo. La domanda «quali consensi ha questa prestazione»
 * aveva risposta; «a cosa e' legato questo consenso» no.
 *
 * LO STATO NON E' UNA COLONNA: si ricava. `consensi_template` ha solo
 * `attivo`, vero o falso, e gli stati sono tre. Un modello spento che
 * nessuno ha mai firmato e' una BOZZA; uno spento su cui esistono
 * documenti firmati e' RITIRATO, e quei documenti restano validi. La
 * differenza conta: la prima si puo' cancellare, la seconda no.
 *
 * QUELLO CHE NON C'E', e non e' una dimenticanza: la nota breve sotto il
 * nome («estetica», «alto rischio vascolare»). Quel campo non esiste.
 * `consensi_template.note` c'e' ma e' occupato dal caricatore dei
 * markdown su 54 modelli su 58, e ricavare l'etichetta da altro -
 * categoria, titolo, prestazioni - vorrebbe dire inventarla.
 */

const VISTE = [
    { chiave: 'tutti',    etichetta: 'Tutti' },
    { chiave: 'attivo',   etichetta: 'Attivi' },
    { chiave: 'bozza',    etichetta: 'Bozze' },
    { chiave: 'ritirato', etichetta: 'Ritirati' },
];

const dataIta = (v) => {
    if (!v) return null;
    const d = new Date(String(v).slice(0, 10));
    return isNaN(d) ? null : d.toLocaleDateString('it-IT');
};

const ModelliConsenso = () => {
    const navigate = useNavigate();
    /* Quanti pazienti stanno proseguendo un ciclo su una versione
       superata. Lo stesso numero che la schermata dedicata somma per
       modello: qui serve solo il totale, per sapere se mostrare la
       porta. Zero: niente pulsante, perche' un pulsante che porta a
       un elenco vuoto insegna a non premerlo. */
    const [daReinformare, setDaReinformare] = useState(0);
    const [modelli, setModelli] = useState([]);
    const [vista, setVista] = useState('tutti');
    const [ricerca, setRicerca] = useState('');
    const [aperto, setAperto] = useState(null);       // id del modello selezionato
    const [prestazioni, setPrestazioni] = useState([]);
    const [caricaPrest, setCaricaPrest] = useState(false);
    const [caricando, setCaricando] = useState(true);
    const [errore, setErrore] = useState(null);
    const [editor, setEditor] = useState(false);
    /* Il modello aperto nell'editor dal clic sulla riga (Alex, 2/11/2026:
       «Sui consensi devo poter cliccare e si deve aprire il relativo
       consenso con l'editor di testo per modificarlo»). Come si apre lo
       dice la rotta dell'elenco, riga per riga (`editor`): 'modifica' per
       una bozza o un modello attivo, 'lettura' per un ritirato, null per chi
       quel modello non lo puo' modificare - a lui la riga mostra le
       prestazioni legate, come prima. La regola resta una, nel server. */
    const [inModifica, setInModifica] = useState(null);   // { id, stato }
    const apriRiga = (m) => {
        if (m.editor) setInModifica({ id: m.id, stato: m.stato });
        else setAperto(aperto === m.id ? null : m.id);
    };
    /* Le azioni della barra comandi (Ctrl K) e di «Nuovo»: lo stesso gesto
       del pulsante della pagina (navigazione/useAzione.js). */
    useAzione({ 'nuovo-modello': () => setEditor(true) });
    /* Le lacune del blocco sul consenso: solo per l'amministrazione, che e'
       chi collega i modelli e li assegna ai medici. */
    const ruolo = useAuthStore((st) => st.user?.ruolo);
    const [lacune, setLacune] = useState(null);
    /* Le letture di apertura riprovano da sole una volta se la risposta non
       arriva (il fermo di un rilascio di giorno), poi il motivo con
       «Riprova» (primaLettura.js). Qui `errore` e' solo della lettura: i
       cambi di stato dicono le notifiche. */
    const caricaLacune = useCallback(() => {
        if (ruolo !== 'admin') return;
        conUnTentativo(() => api.get('/template/lacune'))
            .then((r) => setLacune(r.data?.data || null))
            .catch((e) => setLacune({ errore: motivoDellaLettura(e, 'Non riesco a calcolare l’elenco delle lacune') }));
    }, [ruolo]);
    useEffect(() => { caricaLacune(); }, [caricaLacune]);

    const carica = useCallback(async () => {
        setCaricando(true);
        try {
            const r = await conUnTentativo(() => api.get('/template/elenco'));
            setModelli(r.data?.data?.modelli || []);
            setErrore(null);
        } catch (e) {
            setErrore(motivoDellaLettura(e, 'Non riesco a leggere l’elenco dei modelli'));
        } finally {
            setCaricando(false);
        }
    }, []);

    useEffect(() => {
        conUnTentativo(() => api.get('/versioni-consenso/modelli'))
            .then((r) => setDaReinformare(
                (r.data?.data?.modelli || [])
                    .reduce((t, x) => t + (Number(x.da_informare) || 0), 0)))
            .catch(() => { /* il pulsante resta nascosto */ });
    }, []);

    useEffect(() => { carica(); }, [carica]);

    /* «Attiva» e «Ritira»: solo l'amministrazione (Alex, 3/11/2026; la
       regola la fa rispettare la rotta). Da spento il modello non si sceglie
       e non si firma da nessuna strada, nemmeno da una sessione del tablet
       gia' aperta. */
    const [cambiando, setCambiando] = useState(null);
    const cambiaStato = async (m) => {
        const accendi = m.stato !== 'attivo';
        if (!accendi && !window.confirm(`Ritirare «${m.titolo}»?\n\nDa adesso non si sceglie e non si firma più, nemmeno dal tablet o dalla firma avanzata già avviati. I documenti già firmati restano validi.`)) return;
        setCambiando(m.id);
        try {
            const r = await api.post(`/template/${m.id}/${accendi ? 'attiva' : 'ritira'}`);
            notificaConferma(r.data?.message);
            await carica();
        } catch (e) {
            notificaErrore(messaggioDa(e, 'Non sono riuscito a cambiare lo stato del modello'));
        } finally {
            setCambiando(null);
        }
    };

    /* Le prestazioni si chiedono SOLO per il modello aperto. Caricarle
       per tutti e cinquantotto in anticipo vorrebbe dire cinquantotto
       richieste per una tabella che si guarda una riga per volta. */
    useEffect(() => {
        if (!aperto) { setPrestazioni([]); return; }
        let vivo = true;
        setCaricaPrest(true);
        api.get(`/template/${aperto}/prestazioni`)
            .then((r) => { if (vivo) setPrestazioni(r.data?.data?.prestazioni || []); })
            .catch(() => { if (vivo) setPrestazioni([]); })
            .finally(() => { if (vivo) setCaricaPrest(false); });
        return () => { vivo = false; };
    }, [aperto]);

    const conteggi = useMemo(() => {
        const c = { tutti: modelli.length, attivo: 0, bozza: 0, ritirato: 0 };
        modelli.forEach((m) => { c[m.stato] = (c[m.stato] || 0) + 1; });
        return c;
    }, [modelli]);

    /* La ricerca si applica DOPO la linguetta, non al posto suo: cercare
       «botulino» fra i Bozze deve dare i bozze che si chiamano cosi', non
       riportare in elenco quelli attivi. I contatori sulle linguette
       restano quelli veri, non filtrati: se cambiassero con la ricerca
       non direbbero piu' quanti modelli esistono in ogni stato.

       Si cerca su titolo e ruolo - «principale», «revoca», «minorenni» -
       ignorando maiuscole E ACCENTI: diciassette dei cinquantotto titoli
       ne hanno uno, e chi cerca «perche» o «piu» non deve indovinare
       dove cade l'accento per trovare il modulo. */
    const senzaAccenti = (t) => String(t || '')
        .normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

    const cerca = senzaAccenti(ricerca.trim());
    const mostrati = (vista === 'tutti' ? modelli : modelli.filter((m) => m.stato === vista))
        .filter((m) => !cerca
            || senzaAccenti(`${m.titolo || ''} ${m.ruolo || ''}`).includes(cerca));
    const selezionato = modelli.find((m) => m.id === aperto);

    return (
        <div className="mdc-pagina">
            <IntestazioneSezione
                /* Nessuna etichetta di sezione: questa voce sta nel menu
                   accanto a Prestazioni e NON dentro Amministrazione - lo
                   dice il commento in Layout, ed e' una scelta, non un
                   caso. Scriverci sopra «Amministrazione» raccontava una
                   struttura che non esiste. */
                titolo="Modelli di consenso"
                icona={<FileSignature size={22} aria-hidden="true" />}
                viste={VISTE.map((v) => ({ ...v, contatore: conteggi[v.chiave] || 0 }))}
                vista={vista}
                onVista={(v) => { setVista(v); setAperto(null); }}
                azione={
                    <div className="mdc-azioni">
                        {/* Chi ha un ciclo aperto su una versione superata va
                            reinformato prima della seduta successiva. Il
                            conteggio il server lo calcolava gia': fino al 19
                            settembre 2026 non lo mostrava nessuno, e la
                            schermata che lo dice non aveva nessuna porta. */}
                        {daReinformare > 0 && (
                            <button className="mdc-reinformare"
                                    onClick={() => navigate('/consensi-da-reinformare')}
                                    title="Pazienti che proseguono un ciclo su una versione precedente">
                                <AlertTriangle size={15} aria-hidden="true" />
                                Da reinformare
                                <span className="mdc-pallino-avviso">{daReinformare}</span>
                            </button>
                        )}
                        <button className="mdc-nuovo" onClick={() => setEditor(true)}>
                            <Plus size={16} aria-hidden="true" /> Nuovo modello
                        </button>
                    </div>
                }
            />

            <ErroreLettura testo={errore} onRiprova={carica} classe="mdc-errore" />

            {/* Dal 22 settembre 2026 una prestazione collegata a un consenso
                obbligatorio non si registra se il medico che la esegue non
                ha un modello visibile. Questo e' l'elenco da portare a zero
                prima di gennaio: ogni riga «no» e' una prestazione che quel
                medico non riuscira' a registrare. */}
            {ruolo === 'admin' && lacune && (
                <section className={`mdc-lacune${lacune.lacune ? ' mdc-lacune--aperte' : ''}`}>
                    <h2 className="mdc-lacune-titolo">
                        <AlertTriangle size={16} aria-hidden="true" />
                        Consensi obbligatori: chi non potrà registrare la prestazione
                    </h2>
                    {lacune.errore ? (
                        <ErroreLettura testo={lacune.errore} onRiprova={caricaLacune} classe="mdc-lacune-intro" />
                    ) : (
                        <>
                            <p className="mdc-lacune-intro">
                                Una prestazione collegata a un consenso obbligatorio non si registra se il
                                medico che la esegue non ha un modello visibile — di struttura o suo.{' '}
                                {lacune.lacune
                                    ? <strong>{lacune.lacune === 1 ? '1 coppia' : `${lacune.lacune} coppie`} da sistemare.</strong>
                                    : 'Nessuna lacuna: ogni medico che esegue queste prestazioni ha un modello.'}
                            </p>
                            {(lacune.coppie || []).length > 0 && (
                                <div className="mdc-tabella-contenitore rm-scorre">
                                    <table className="mdc-tabella">
                                        <thead>
                                            <tr>
                                                <th>Prestazione</th>
                                                <th>Medico</th>
                                                <th>Perché compare</th>
                                                <th>Modello visibile</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {lacune.coppie.map((c) => (
                                                <tr key={`${c.prestazione_id}-${c.medico_id}`}>
                                                    <td>
                                                        {c.prestazione}
                                                        {!c.prestazione_attiva && <span className="mdc-spenta"> · non attiva</span>}
                                                    </td>
                                                    <td>
                                                        {[c.medico_titolo, c.medico_cognome, c.medico_nome].filter(Boolean).join(' ')}
                                                        {!c.medico_attivo && <span className="mdc-spenta"> · non attivo</span>}
                                                    </td>
                                                    <td className="mdc-data">{c.fonti}</td>
                                                    <td className={c.ha_modello ? 'mdc-lacuna-si' : 'mdc-lacuna-no'}>
                                                        {c.ha_modello ? 'sì' : 'no'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </>
                    )}
                </section>
            )}

            <div className="mdc-cerca">
                <Search size={15} aria-hidden="true" />
                <input
                    type="search"
                    value={ricerca}
                    onChange={(e) => { setRicerca(e.target.value); setAperto(null); }}
                    placeholder="Cerca per nome o tipo di modulo…"
                    aria-label="Cerca fra i modelli di consenso"
                />
                {ricerca && (
                    <button type="button" onClick={() => setRicerca('')} aria-label="Pulisci la ricerca">
                        <X size={14} aria-hidden="true" />
                    </button>
                )}
            </div>

            {caricando ? <p className="mdc-attesa">Carico…</p> : mostrati.length === 0 ? (
                /* Due vuoti diversi: «non ce ne sono» e «la ricerca non
                   trova niente» si risolvono in due modi opposti, e un
                   messaggio solo manderebbe a cercare il problema dalla
                   parte sbagliata. */
                <p className="mdc-vuoto">
                    {cerca
                        ? `Nessun modello trovato per «${ricerca.trim()}» in questa vista.`
                        : 'Nessun modello in questa vista.'}
                </p>
            ) : (
                <div className="mdc-tabella-contenitore rm-scorre">
                    <table className="mdc-tabella">
                        <thead>
                            <tr>
                                <th>Modello</th>
                                <th>Versione</th>
                                <th className="num">Blocchi</th>
                                <th className="num">Firme</th>
                                <th className="num">Prestazioni</th>
                                <th>Stato</th>
                            </tr>
                        </thead>
                        <tbody>
                            {mostrati.map((m) => (
                                <tr key={m.id}
                                    className={`mdc-riga${aperto === m.id ? ' aperta' : ''}`}
                                    data-modello={m.id}
                                    /* La riga si apre anche da tastiera: si raggiunge
                                       col Tab e si apre con Invio. Solo l'Invio sulla
                                       riga stessa: quello sul pulsante delle
                                       prestazioni e' del pulsante. */
                                    tabIndex={0}
                                    title={m.editor === 'modifica' ? 'Apri il modello per modificarlo'
                                        : m.editor === 'lettura' ? 'Apri il modello (ritirato: sola lettura)'
                                            : undefined}
                                    onClick={() => apriRiga(m)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && e.target === e.currentTarget) {
                                            e.preventDefault();
                                            apriRiga(m);
                                        }
                                    }}>
                                    <td>
                                        <span className="mdc-nome">{m.titolo}</span>
                                        {m.ruolo !== 'principale' && (
                                            /* I moduli staccabili - revoca, minorenni - non
                                               sono consensi a se': dirlo evita di contarli
                                               come se lo fossero. */
                                            <span className="mdc-modulo">modulo · {m.ruolo}</span>
                                        )}
                                        {m.medico_cognome && (
                                            <span className="mdc-medico">
                                                di {m.medico_cognome} {m.medico_nome}
                                            </span>
                                        )}
                                    </td>
                                    <td className="mdc-versione">
                                        v{m.versione}
                                        {m.versione_data && (
                                            <span className="mdc-data"> del {dataIta(m.versione_data)}</span>
                                        )}
                                    </td>
                                    <td className="num">{m.blocchi}</td>
                                    <td className="num">{m.firme}</td>
                                    <td className="num">
                                        {m.prestazioni > 0 ? (
                                            <button className="mdc-conteggio"
                                                    onClick={(e) => { e.stopPropagation(); setAperto(m.id); }}>
                                                {m.prestazioni} <ChevronRight size={13} aria-hidden="true" />
                                            </button>
                                        ) : (
                                            /* Zero prestazioni su un modello ATTIVO e' il difetto
                                               piu' silenzioso che ci sia: il consenso non viene
                                               mai proposto e nessuno se ne accorge. */
                                            <span className={m.stato === 'attivo' ? 'mdc-zero-grave' : 'mdc-zero'}>
                                                0
                                            </span>
                                        )}
                                    </td>
                                    <td>
                                        <span className={`mdc-stato mdc-stato-${m.stato}`}>{m.stato}</span>
                                        {m.stato === 'ritirato' && m.firmati > 0 && (
                                            <span className="mdc-firmati">{m.firmati} firmati</span>
                                        )}
                                        {ruolo === 'admin' && (
                                            <button type="button" className="mdc-stato-cambia"
                                                    disabled={cambiando === m.id}
                                                    onClick={(e) => { e.stopPropagation(); cambiaStato(m); }}
                                                    onKeyDown={(e) => e.stopPropagation()}>
                                                {m.stato === 'attivo' ? 'Ritira' : 'Attiva'}
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {selezionato && (
                <section className="mdc-dettaglio">
                    <h2>
                        <Link2 size={16} aria-hidden="true" />
                        Prestazioni legate a «{selezionato.titolo}»
                    </h2>
                    {caricaPrest ? (
                        <p className="mdc-attesa">Carico…</p>
                    ) : prestazioni.length === 0 ? (
                        <p className={selezionato.stato === 'attivo' ? 'mdc-avviso' : 'mdc-vuoto'}>
                            {selezionato.stato === 'attivo'
                                ? 'Nessuna prestazione collegata: questo modello è attivo ma non verrà mai proposto a nessuno.'
                                : 'Nessuna prestazione collegata.'}
                        </p>
                    ) : (
                        <ul className="mdc-prestazioni">
                            {prestazioni.map((p) => (
                                <li key={p.id}>
                                    <span className="mdc-p-nome">{p.nome}</span>
                                    <span className="mdc-p-dati">
                                        {p.codice && <span>{p.codice}</span>}
                                        {p.macro_area && <span>{p.macro_area}</span>}
                                        {p.obbligatorio && <span className="mdc-obbl">obbligatorio</span>}
                                        {!p.attivo && <span className="mdc-spenta">prestazione non attiva</span>}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>
            )}

            {editor && (
                <EditorTemplate
                    onClose={() => setEditor(false)}
                    onSuccess={() => { setEditor(false); carica(); }}
                />
            )}

            {inModifica && (
                <EditorTemplate
                    key={inModifica.id}
                    templateId={inModifica.id}
                    stato={inModifica.stato}
                    onClose={() => setInModifica(null)}
                    onSuccess={() => { setInModifica(null); carica(); }}
                />
            )}
        </div>
    );
};

export default ModelliConsenso;
